import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * SearchableSelect — a type-to-search dropdown for long lists (countries, nationalities).
 * Options can be plain strings or { label, value } objects.
 */
export default function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Type to search...',
  className,
  'data-testid': testId,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  // Normalize options to { label, value } format
  const normalized = options.map((opt) =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt
  );

  const filtered = search
    ? normalized.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()))
    : normalized;

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearch('');
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal h-12 rounded-xl border-2',
            !value && 'text-muted-foreground',
            className
          )}
          data-testid={testId}
        >
          <span className="truncate">
            {normalized.find((o) => o.value === value)?.label || value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-[240px] overflow-y-auto p-1">
          {filtered.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">No results found</p>
          )}
          {filtered.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                value === opt.value && 'bg-accent text-accent-foreground'
              )}
              onClick={() => {
                onValueChange(opt.value === value ? '' : opt.value);
                setOpen(false);
              }}
            >
              <Check
                className={cn('mr-2 h-4 w-4', value === opt.value ? 'opacity-100' : 'opacity-0')}
              />
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
